import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { X, Image as ImageIcon, Loader } from 'lucide-react';
import { getUserArtworkAcrossCircles } from '../services/galleryService';

export default function MyGalleryModal({ isOpen, onClose, onSelectImage, currentUserId }) {
  const [artwork, setArtwork] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);

  useEffect(() => {
    if (isOpen && currentUserId) {
      loadUserArtwork();
    }
  }, [isOpen, currentUserId]);

  const loadUserArtwork = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const userArtwork = await getUserArtworkAcrossCircles(currentUserId);
      setArtwork(userArtwork);
      if (userArtwork.length === 0) {
        setError('No artwork found. Create some art first!');
      }
    } catch (err) {
      console.error('Failed to load artwork:', err);
      setError('Failed to load your gallery. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageClick = (artwork) => {
    setSelectedImageUrl(artwork.fullResImageUrl || artwork.imageUrl);
  };

  const handleConfirmSelection = () => {
    if (selectedImageUrl) {
      onSelectImage(selectedImageUrl);
      onClose();
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <Overlay onClick={handleBackdropClick}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>
            <ImageIcon size={20} />
            My Gallery
          </Title>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </Header>

        {isLoading && (
          <LoadingState>
            <Loader className="spinner" size={32} />
            <p>Loading your artwork...</p>
          </LoadingState>
        )}

        {error && (
          <ErrorState>
            <p>{error}</p>
            <RetryButton onClick={loadUserArtwork}>Retry</RetryButton>
          </ErrorState>
        )}

        {!isLoading && !error && artwork.length > 0 && (
          <>
            <Subtitle>
              Select an image from your past creations across all circles
            </Subtitle>
            <GalleryGrid>
              {artwork.map((art) => (
                <ArtworkCard
                  key={art.id}
                  onClick={() => handleImageClick(art)}
                  $isSelected={selectedImageUrl === (art.fullResImageUrl || art.imageUrl)}
                >
                  <ArtworkImage src={art.imageUrl} alt="Your artwork" loading="lazy" />
                  <ArtworkOverlay $isSelected={selectedImageUrl === (art.fullResImageUrl || art.imageUrl)}>
                    {selectedImageUrl === (art.fullResImageUrl || art.imageUrl) && (
                      <SelectionIndicator>✓</SelectionIndicator>
                    )}
                  </ArtworkOverlay>
                  <CircleLabel>{art.circleName}</CircleLabel>
                </ArtworkCard>
              ))}
            </GalleryGrid>
            
            <Footer>
              <CancelButton onClick={onClose}>Cancel</CancelButton>
              <ConfirmButton 
                onClick={handleConfirmSelection} 
                disabled={!selectedImageUrl}
              >
                Use Selected Image
              </ConfirmButton>
            </Footer>
          </>
        )}
      </ModalContainer>
    </Overlay>
  );
}

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 700px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  
  @media (min-width: 500px) {
    padding: 20px 24px;
  }
`;

const Title = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  color: #111827;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.2s;

  &:hover {
    background: #f3f4f6;
    color: #111827;
  }
`;

const Subtitle = styled.p`
  font-size: 0.8125rem;
  color: #6b7280;
  margin: 0;
  padding: 12px 20px 0;
  
  @media (min-width: 500px) {
    padding: 12px 24px 0;
  }
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  padding: 16px 20px 20px;
  overflow-y: auto;
  flex: 1;
  
  @media (min-width: 500px) {
    gap: 14px;
    padding: 16px 24px 24px;
  }
`;

const ArtworkCard = styled.div`
  position: relative;
  aspect-ratio: 1;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  background: #f9fafb;
  border: ${props => props.$isSelected ? '3px solid #f59e0b' : '1px solid #e5e7eb'};
  transition: all 0.2s;
  
  &:hover {
    border-color: ${props => props.$isSelected ? '#f59e0b' : '#d1d5db'};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const ArtworkImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ArtworkOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => props.$isSelected ? 'rgba(245, 158, 11, 0.2)' : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
`;

const SelectionIndicator = styled.div`
  width: 32px;
  height: 32px;
  background: #f59e0b;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.125rem;
  font-weight: bold;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
`;

const CircleLabel = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.65) 0%, rgba(0, 0, 0, 0.3) 60%, transparent 100%);
  color: white;
  font-size: 11px;
  padding: 20px 8px 6px;
  text-align: center;
  font-weight: 500;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: #6b7280;
  
  .spinner {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  
  p {
    margin-top: 1rem;
  }
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: #ef4444;
  
  p {
    margin-bottom: 1rem;
  }
`;

const RetryButton = styled.button`
  background: #f59e0b;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: #d97706;
  }
`;

const Footer = styled.div`
  display: flex;
  gap: 10px;
  padding: 16px 20px;
  border-top: 1px solid #e5e7eb;
  
  @media (min-width: 500px) {
    padding: 20px 24px;
  }
`;

const CancelButton = styled.button`
  flex: 1;
  background: #f3f4f6;
  color: #374151;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 10px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #e5e7eb;
  }
`;

const ConfirmButton = styled.button`
  flex: 2;
  background: #f59e0b;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 10px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
  
  &:hover:not(:disabled) {
    background: #d97706;
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(245, 158, 11, 0.4);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
